import {
  DebtorPayment,
} from '../api'
import {
  BrokerPaymentV2,
} from '../api-v2'
import { mapBrokerPaymentV2ResponseToV1Response, mapBrokerPaymentsV2ResponseToV1Response, mapCreateBrokerPaymentV1RequestToV2Request, mapUpdateBrokerPaymentV1RequestToV2Request } from "../api-v2/mappings"
import { ApiVersionDelagator } from "./api-delegator"
/**
 * @typedef {import('../api-v2/js-doc').V1CreateBrokerPaymentRequest} V1CreateBrokerPaymentRequest
 * @typedef {import('../api-v2/js-doc').V1UpdateBrokerPaymentRequest} V1UpdateBrokerPaymentRequest
 * @typedef {import('../api-v2/js-doc').V1BrokerPaymentResponse} V1BrokerPaymentResponse
 * @typedef {import('../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 */

export class BrokerPaymentApiDelegator extends ApiVersionDelagator {
  /**
   * @param {string} invoiceId - ID of the invoice
   * @returns {Promise<V1BrokerPaymentResponse[]>}
   */
  async getBrokerPayments (invoiceId) {
    return this.runGetApiCalls(() => {
      return this.#getBrokerPaymentsV1(invoiceId)
    }, () => {
      return this.#getBrokerPaymentsV2(invoiceId)
    })
  }

  /**
   * @param {string} invoiceId - ID of the invoice
   * @returns {Promise<V1BrokerPaymentResponse[]>}
   */
  async #getBrokerPaymentsV1 (invoiceId) {
    return (await DebtorPayment.queryList({
      invoice_id: invoiceId,
      sort_by: 'created_at',
      sort_direction: 'ASC',
    })).data.rows
  }

  /**
   * @param {string} invoiceId - ID of the invoice
   * @returns {Promise<V1BrokerPaymentResponse[]>}
   */
  async #getBrokerPaymentsV2 (invoiceId) {
    const items = await new BrokerPaymentV2().list(invoiceId)
    return mapBrokerPaymentsV2ResponseToV1Response(items)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1CreateBrokerPaymentRequest} v1Request - V1 create broker payment request
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async postBrokerPayment (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#postBrokerPaymentV1,
      this.#postBrokerPaymentV2,
      {
        operationName: 'post-broker-payment',
      }
    )
  }

  /**
   * @param {V1CreateBrokerPaymentRequest} v1Request - V1 create broker payment request
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async #postBrokerPaymentV1 (v1Request) {
    return (await DebtorPayment.create(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1CreateBrokerPaymentRequest} v1Request - V1 create broker payment request
   * @param {V1BrokerPaymentResponse=} v1Response - V1 create broker payment body
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async #postBrokerPaymentV2 (v2MappingOptions, v1Request, v1Response) {
    const v2Request = mapCreateBrokerPaymentV1RequestToV2Request(v2MappingOptions, v1Request, v1Response)
    const result = await new BrokerPaymentV2().create(v2Request)
    return mapBrokerPaymentV2ResponseToV1Response(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {string} id - ID of the broker payment
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async deleteBrokerPayment (clientId, id) {
    return await this.runWriteApiCalls(
      clientId,
      id,
      this.#deleteBrokerPaymentV1,
      this.#deleteBrokerPaymentV2,
      {
        operationName: 'delete-broker-payment',
      }
    )
  }

  /**
   * @param {string} id - ID of the broker payment
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async #deleteBrokerPaymentV1 (id) {
    return (await DebtorPayment.delete(id)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {string} id - ID of the broker payment
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async #deleteBrokerPaymentV2 (v2MappingOptions, id) {
    const result = await new BrokerPaymentV2().delete(id, { __ingestThrough: v2MappingOptions.ingestThrough })
    return mapBrokerPaymentV2ResponseToV1Response(result)
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1UpdateBrokerPaymentRequest} v1Request - V1 update broker payment request
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async updateBrokerPayment (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#putDebtorPaymentV1,
      this.#putDebtorPaymentV2,
      {
        operationName: 'update-broker-payment',
      }
    )
  }

  /**
   * @param {V1UpdateBrokerPaymentRequest} v1Request - V1 update broker payment request
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async #putDebtorPaymentV1 (v1Request) {
    return (await DebtorPayment.update(v1Request)).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1UpdateBrokerPaymentRequest} v1Request - V1 update broker payment request
   * @returns {Promise<V1BrokerPaymentResponse>}
   */
  async #putDebtorPaymentV2 (v2MappingOptions, v1Request) {
    const requestBody = mapUpdateBrokerPaymentV1RequestToV2Request(v2MappingOptions, v1Request)
    const result = await new BrokerPaymentV2().update(v1Request.id, requestBody)
    return mapBrokerPaymentV2ResponseToV1Response(result)
  }
}
